import { useState } from 'react';
import { CSSProperties } from 'react';
import {
    Control,
    Controller,
    FieldError,
    FieldValues,
    Path,
    ControllerProps,
} from 'react-hook-form';
import {
    TextFieldProps,
    IconButton,
    InputAdornment,
    InputBaseComponentProps,
} from '@mui/material';
import { ISize, RegexMapOptions } from '../interface';
import { REGEX_MAP } from '../utils';
import {
    Icon,
    Stack,
    useTheme,
    Theme,
    PargodaInfoIcon,
} from '../../base-components';
import { CustomTextField } from './FormInputText.styles';
import FormFieldMaxCharacterCount from '../FormFieldMaxCharacterCount';
import { TooltipPlacement } from '../../../common';

export interface IFormInputText<F extends FieldValues> {
    name: Path<F>;
    control: Control<F>;
    rules?: ControllerProps<F>['rules'];
    label?: string;
    required?: boolean;
    disabled?: boolean;
    readOnly?: boolean;
    size?: ISize;
    inputProps?: InputBaseComponentProps & {
        readOnly?: boolean;
    };
    InputProps?: {
        startAdornment?: React.ReactNode;
        endAdornment?: React.ReactNode;
        [key: string]: unknown;
    };
    placeholder?: string;
    validateExpression?: RegexMapOptions;
    rows?: number;
    multiline?: boolean;
    inputType?: TextFieldProps['type'];
    style?: CSSProperties;
    autoFocus?: boolean;
    dataTestId?: string;
    allowMoreCharactersThanMax?: boolean;
    hideCharacterCount?: boolean;
    sx?: CSSProperties;
    isTextAlignCenter?: boolean;
    removeBottomPadding?: boolean;
    tooltipEndAdornment?: {
        text: string;
        color?: string;
        location?: TooltipPlacement;
    };
    displayErrorInEndAdornment?: boolean;
}

const getPasswordEndAdornment = (
    showPassword: boolean,
    handleClickShowPassword: () => void,
    handleMouseDownPassword: (
        event: React.MouseEvent<HTMLButtonElement>
    ) => void,
    theme: Theme,
    size: string
) => (
    <InputAdornment position="end">
        <IconButton
            aria-label="toggle password visibility"
            onClick={handleClickShowPassword}
            onMouseDown={handleMouseDownPassword}
            edge="end"
            size={size === 'small' ? 'small' : 'medium'}
        >
            {showPassword ? (
                <Icon
                    name={'VisibilityOffOutlinedIcon'}
                    color={theme.palette.grey[500]}
                />
            ) : (
                <Icon
                    name={'VisibilityOutlinedIcon'}
                    color={theme.palette.grey[500]}
                />
            )}
        </IconButton>
    </InputAdornment>
);

export const FormInputText = <F extends FieldValues>({
    name,
    control,
    rules,
    label,
    inputType,
    required = false,
    disabled = false,
    readOnly = false,
    size = 'small',
    inputProps,
    InputProps,
    placeholder,
    validateExpression,
    rows,
    multiline = false,
    style,
    autoFocus,
    dataTestId,
    allowMoreCharactersThanMax = false,
    hideCharacterCount = false,
    sx,
    isTextAlignCenter,
    removeBottomPadding = false,
    tooltipEndAdornment,
    displayErrorInEndAdornment = false,
}: IFormInputText<F>) => {
    const [showPassword, setShowPassword] = useState(false);
    const handleClickShowPassword = () => setShowPassword((show) => !show);
    const handleMouseDownPassword = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        event.preventDefault();
    };

    const theme = useTheme();
    const maxCharacterCount = inputProps?.maxLength;
    const showMaxCharacterCount = !!maxCharacterCount && !hideCharacterCount;
    if (maxCharacterCount && allowMoreCharactersThanMax) {
        inputProps.maxLength = null;
    }

    const renderTooltip = (
        text: string | undefined,
        location?: TooltipPlacement | undefined,
        color?: string | undefined,
        iconName?: string | undefined
    ) => (
        <InputAdornment position="start">
            <PargodaInfoIcon
                text={text ?? ''}
                placement={location}
                color={color}
                iconName={iconName}
            />
        </InputAdornment>
    );

    const getFinalEndAdornment = (error: FieldError | undefined) => {
        if (error && displayErrorInEndAdornment) {
            return renderTooltip(
                error.message,
                TooltipPlacement.TOP_START,
                theme.palette.error.main,
                'ErrorOutlineOutlinedIcon'
            );
        } else if (inputType === 'password') {
            return getPasswordEndAdornment(
                showPassword,
                handleClickShowPassword,
                handleMouseDownPassword,
                theme,
                size
            );
        } else if (tooltipEndAdornment) {
            return renderTooltip(
                tooltipEndAdornment.text,
                tooltipEndAdornment.location,
                tooltipEndAdornment.color
            );
        }
        return InputProps?.endAdornment;
    };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState: { error } }) => (
                <Stack
                    width="100%"
                    sx={{
                        paddingBottom: removeBottomPadding ? undefined : '10px',
                        ...sx,
                    }}
                >
                    <CustomTextField
                        {...field}
                        value={field.value}
                        onChange={(e) => {
                            if (
                                validateExpression &&
                                REGEX_MAP[
                                    validateExpression as keyof typeof REGEX_MAP
                                ] &&
                                !REGEX_MAP[
                                    validateExpression as keyof typeof REGEX_MAP
                                ].test(e.target.value)
                            ) {
                                e.target.value = '';
                            } else {
                                field.onChange(e);
                            }
                        }}
                        onBlur={field.onBlur}
                        data-testid={dataTestId}
                        size={size}
                        fullWidth
                        variant="outlined"
                        type={
                            inputType === 'password' && showPassword
                                ? 'text'
                                : inputType
                        }
                        label={label && (required ? `${label} *` : label)}
                        disabled={disabled}
                        error={Boolean(error)}
                        helperText={
                            error &&
                            !displayErrorInEndAdornment &&
                            error.message
                        }
                        style={style}
                        inputProps={{
                            ...inputProps,
                            readOnly,
                        }}
                        InputProps={{
                            ...InputProps,
                            endAdornment: getFinalEndAdornment(error),
                        }}
                        placeholder={
                            placeholder &&
                            (required ? `${placeholder} *` : placeholder)
                        }
                        rows={rows}
                        multiline={multiline}
                        autoFocus={autoFocus}
                        sx={{
                            '& .MuiInputBase-root': {
                                borderRadius: '15px',
                            },
                            '& .MuiInputBase-input': {
                                textAlign: isTextAlignCenter
                                    ? 'center'
                                    : 'inherit',
                            },
                        }}
                    />
                    {showMaxCharacterCount && (
                        <FormFieldMaxCharacterCount
                            fieldValue={field.value}
                            maxCharacterCount={maxCharacterCount}
                        />
                    )}
                </Stack>
            )}
        />
    );
};

export default FormInputText;
